import type { ReactNode } from "react";
import { StyleSheet, View } from "react-native";

import {
  radii,
  shadow,
  spacing,
  useThemedStyles,
  type ThemeColors,
} from "~/styles/theme";
import type { Space } from "./types";

export function Card({
  children,
  danger = false,
  accent = "none",
  compact = false,
  gap,
}: {
  children: ReactNode;
  danger?: boolean;
  /** Soft left accent for metrics / emphasis without double nesting. */
  accent?: "none" | "mint" | "danger";
  compact?: boolean;
  gap?: Space;
}) {
  const { stylesheet, gapStyles } = useThemedStyles(makeStyles);
  const resolvedAccent = danger ? "danger" : accent;

  return (
    <View
      style={[
        stylesheet.card,
        shadow("sm"),
        resolvedAccent === "mint" ? stylesheet.mint : null,
        resolvedAccent === "danger" ? stylesheet.danger : null,
        compact ? stylesheet.compact : null,
        gap ? gapStyles[gap] : null,
      ]}
    >
      {children}
    </View>
  );
}

const makeStyles = (colors: ThemeColors) => {
  const stylesheet = StyleSheet.create({
    card: {
      padding: spacing.l,
      borderWidth: 1,
      borderColor: colors.borderStrong,
      borderRadius: radii.card,
      backgroundColor: colors.elevated,
    },
    mint: {
      borderLeftWidth: 3,
      borderLeftColor: colors.primary,
    },
    danger: {
      borderColor: colors.danger,
      borderLeftWidth: 3,
      borderLeftColor: colors.danger,
      backgroundColor: colors.dangerSurface,
    },
    compact: { padding: spacing.m },
    gapNone: { gap: 0 },
    gapXs: { gap: spacing.xs },
    gapSm: { gap: spacing.s },
    gapMd: { gap: spacing.m },
    gapLg: { gap: spacing.l },
  });
  return {
    stylesheet,
    gapStyles: {
      none: stylesheet.gapNone,
      xs: stylesheet.gapXs,
      sm: stylesheet.gapSm,
      md: stylesheet.gapMd,
      lg: stylesheet.gapLg,
    },
  };
};
